import { ReactNode, useCallback } from 'react';
import {
    getVoucherStatusColor,
    Voucher,
    getVoucherPurchasedBy,
    getVoucherRedeemedBy,
    getVoucherPurchasedOn,
    getVoucherRedeemedOn,
    getVoucherSalesInterface,
} from '../../model/voucher.types';
import { Box, Chip, ChipProps, Divider, Typography } from '@mui/material';
import { VoucherDrawerLabel } from '../../ui/voucherDrawerContent/voucherDrawerLabel';
import { getMoneyAsString } from '../../../money/lib/utils';
import { formatDateToString } from '../voucherUiUtils';
import { useTranslation } from 'react-i18next';
import { useVoucherLabelTranslations } from './useVoucherLabelTranslations';

export const voucherContentBlockIds = [
    'code',
    'title',
    'sender',
    'receiver',
    'purchasedBy',
    'redeemedBy',
    'purchasedOn',
    'redeemedOn',
    'salesInterface',
    'status',
    'initialAmount',
    'amount',
    'redeemableAmount',
    'voucherId',
    'orderId',
    'validUntil',
    'transactions',
    'revokedAt',
    'refundStatus',
    'refundAmount',
] as const;

export type VoucherContentBlockId = (typeof voucherContentBlockIds)[number];

type VoucherContentBlock = {
    blockId: VoucherContentBlockId;
    header: string;
    content: ReactNode;
};

export const useVoucherDetailsBlocksFactory = () => {
    const { t } = useTranslation();
    const { getVoucherStatusAsString } = useVoucherLabelTranslations();

    return useCallback(
        ({ voucher }: { voucher: Voucher }): VoucherContentBlock[] => [
            {
                blockId: 'code',
                header: t('voucherDetails.code'),
                content: (
                    <VoucherDrawerLabel>
                        {voucher.code || '-'}
                    </VoucherDrawerLabel>
                ),
            },
            {
                blockId: 'title',
                header: t('voucherDetails.title'),
                content: (
                    <VoucherDrawerLabel>
                        {voucher.title || '-'}
                    </VoucherDrawerLabel>
                ),
            },
            {
                blockId: 'sender',
                header: t('voucherDetails.sender'),
                content: (
                    <VoucherDrawerLabel>
                        {voucher.sender || '-'}
                    </VoucherDrawerLabel>
                ),
            },
            {
                blockId: 'receiver',
                header: t('voucherDetails.receiver'),
                content: (
                    <VoucherDrawerLabel>
                        {voucher.receiver || '-'}
                    </VoucherDrawerLabel>
                ),
            },
            {
                blockId: 'purchasedBy',
                header: t('voucherDetails.purchasedBy'),
                content: (
                    <VoucherDrawerLabel>
                        {getVoucherPurchasedBy(voucher) || '-'}
                    </VoucherDrawerLabel>
                ),
            },
            {
                blockId: 'redeemedBy',
                header: t('voucherDetails.redeemedBy'),
                content: (
                    <VoucherDrawerLabel>
                        {getVoucherRedeemedBy(voucher) || '-'}
                    </VoucherDrawerLabel>
                ),
            },
            {
                blockId: 'purchasedOn',
                header: t('voucherDetails.purchasedOn'),
                content: (
                    <VoucherDrawerLabel>
                        {formatDateToString(getVoucherPurchasedOn(voucher), {
                            detailed: true,
                        }) || '-'}
                    </VoucherDrawerLabel>
                ),
            },
            {
                blockId: 'redeemedOn',
                header: t('voucherDetails.redeemedOn'),
                content: (
                    <VoucherDrawerLabel>
                        {getVoucherRedeemedOn(voucher)
                            ? formatDateToString(
                                  getVoucherRedeemedOn(voucher),
                                  {
                                      detailed: true,
                                  }
                              )
                            : '-'}
                    </VoucherDrawerLabel>
                ),
            },
            {
                blockId: 'salesInterface',
                header: t('voucherDetails.salesInterface'),
                content: (
                    <Chip
                        color='primary'
                        label={
                            <VoucherDrawerLabel>
                                {getVoucherSalesInterface(voucher) || '-'}
                            </VoucherDrawerLabel>
                        }
                    />
                ),
            },
            {
                blockId: 'status',
                header: t('voucherDetails.status'),
                content: (
                    <Chip
                        color={getVoucherStatusColor(voucher.status)}
                        label={
                            <VoucherDrawerLabel>
                                {getVoucherStatusAsString(voucher.status)}
                            </VoucherDrawerLabel>
                        }
                    />
                ),
            },
            {
                blockId: 'initialAmount',
                header: t('voucherDetails.initialAmount'),
                content: (
                    <VoucherDrawerLabel>
                        {voucher.initialAmount
                            ? getMoneyAsString(voucher.initialAmount)
                            : '-'}
                    </VoucherDrawerLabel>
                ),
            },
            {
                blockId: 'amount',
                header: t('voucherDetails.amount'),
                content: (
                    <VoucherDrawerLabel>
                        {voucher.amount
                            ? getMoneyAsString(voucher.amount)
                            : '-'}
                    </VoucherDrawerLabel>
                ),
            },
            {
                blockId: 'redeemableAmount',
                header: t('voucherDetails.redeemableAmount'),
                content: (
                    <VoucherDrawerLabel>
                        {voucher.redeemableAmount
                            ? getMoneyAsString(voucher.redeemableAmount)
                            : '-'}
                    </VoucherDrawerLabel>
                ),
            },
            {
                blockId: 'validUntil',
                header: t('voucherDetails.validUntil'),
                content: (
                    <VoucherDrawerLabel>
                        {voucher.validUntil
                            ? formatDateToString(voucher.validUntil)
                            : '-'}
                    </VoucherDrawerLabel>
                ),
            },
            {
                blockId: 'voucherId',
                header: t('voucherDetails.voucherId'),
                content: (
                    <VoucherDrawerLabel>{voucher.id || '-'}</VoucherDrawerLabel>
                ),
            },
            {
                blockId: 'orderId',
                header: t('voucherDetails.orderId'),
                content: (
                    <VoucherDrawerLabel>
                        {voucher.orderId || '-'}
                    </VoucherDrawerLabel>
                ),
            },
            {
                blockId: 'transactions',
                header: t('voucherDetails.transactions'),
                content:
                    voucher.transactions && voucher.transactions.length > 0 ? (
                        <Box>
                            {voucher.transactions.map((transaction, index) => (
                                <Box key={transaction.id}>
                                    {index > 0 && <Divider sx={{ my: 2 }} />}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 1,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Typography
                                                variant='body2'
                                                fontWeight={600}
                                            >
                                                {transaction.reference}
                                            </Typography>
                                            <Chip
                                                label={getMoneyAsString(
                                                    transaction.amount
                                                )}
                                                size='small'
                                                color={
                                                    transaction.amount.amount >=
                                                    0
                                                        ? 'success'
                                                        : 'default'
                                                }
                                            />
                                        </Box>
                                        <VoucherDrawerLabel>
                                            {formatDateToString(
                                                transaction.timestamp,
                                                { detailed: true }
                                            )}
                                        </VoucherDrawerLabel>
                                        <VoucherDrawerLabel>
                                            User: {transaction.userId}
                                        </VoucherDrawerLabel>
                                        <VoucherDrawerLabel>
                                            Environment:{' '}
                                            {transaction.environment}
                                        </VoucherDrawerLabel>
                                        {transaction.orderId && (
                                            <VoucherDrawerLabel>
                                                Order: {transaction.orderId}
                                            </VoucherDrawerLabel>
                                        )}
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        <VoucherDrawerLabel>-</VoucherDrawerLabel>
                    ),
            },
            // Revocation details — only for revoked vouchers, so staff can resolve
            // failed refunds manually (#1856).
            ...(voucher.revokedAt
                ? [
                      {
                          blockId: 'revokedAt' as const,
                          header: t('voucherDetails.revokedAt'),
                          content: (
                              <VoucherDrawerLabel>
                                  {formatDateToString(voucher.revokedAt, {
                                      detailed: true,
                                  })}
                              </VoucherDrawerLabel>
                          ),
                      },
                      {
                          blockId: 'refundStatus' as const,
                          header: t('voucherDetails.refundStatus'),
                          content: (
                              <Chip
                                  color={getRefundStatusColor(
                                      voucher.refundStatus
                                  )}
                                  label={
                                      <VoucherDrawerLabel>
                                          {voucher.refundStatus || '-'}
                                      </VoucherDrawerLabel>
                                  }
                              />
                          ),
                      },
                      {
                          blockId: 'refundAmount' as const,
                          header: t('voucherDetails.refundAmount'),
                          content: (
                              <VoucherDrawerLabel>
                                  {voucher.refundAmount
                                      ? getMoneyAsString(voucher.refundAmount)
                                      : '-'}
                              </VoucherDrawerLabel>
                          ),
                      },
                  ]
                : []),
        ],
        [t, getVoucherStatusAsString]
    );
};

const getRefundStatusColor = (
    refundStatus: string | null
): ChipProps['color'] => {
    switch (refundStatus) {
        case 'COMPLETED':
            return 'success';
        case 'FAILED':
            return 'error';
        case 'PENDING':
            return 'warning';
        default:
            return 'info';
    }
};
